<div class="billing-tab">
  <div *ngIf="isLoading" class="bt-state"><mat-spinner diameter="32"></mat-spinner></div>
  <div *ngIf="!isLoading && loadError" class="bt-state bt-state--error">
    <mat-icon>error_outline</mat-icon><span>{{ loadError }}</span>
  </div>

  <div *ngIf="!isLoading && !loadError" class="bt-body">

    <!-- Header / summary -->
    <header class="bt-header">
      <div class="bt-header__title">
        <mat-icon>receipt_long</mat-icon>
        <div>
          <h3>Billing configuration</h3>
          <p class="bt-sub">How returns are generated and assessed for this account.</p>
        </div>
      </div>
      <div class="bt-header__meta">
        <span class="bt-pill" [ngClass]="'is-' + (form.status || '' | lowercase)">
          <span class="bt-pill__dot"></span>{{ form.status || '—' }}
        </span>
        <span class="bt-chip" *ngIf="config?.lastGeneratedPeriod">
          <mat-icon>event_available</mat-icon>
          Last generated <strong>{{ config?.lastGeneratedPeriod }}</strong>
        </span>
        <span class="bt-chip bt-chip--muted" *ngIf="!config?.lastGeneratedPeriod">
          <mat-icon>schedule</mat-icon> No returns generated yet
        </span>
      </div>
    </header>

    <!-- Properties not yet on returns they cover (e.g. added with a past start date) -->
    <!-- Properties renting before the billing start: no returns exist for those months yet -->
    <div class="bt-alert bt-alert--warn bt-missing" *ngIf="beforeBillingStart.length">
      <mat-icon>event_busy</mat-icon>
      <div class="bt-missing__body">
        <strong>Months before {{ config?.startPeriod }} are not billed yet.</strong>
        <ul>
          <li *ngFor="let b of beforeBillingStart">{{ b.address }} started renting before billing starts ({{ config?.startPeriod }})</li>
        </ul>
        <span *ngIf="config?.status === 'ACTIVE'">Billing from {{ earliestRentalPeriod }} creates the returns for those months.</span>
        <span *ngIf="config?.status !== 'ACTIVE'">Billing is {{ config?.status }}: make it ACTIVE first, then bill from {{ earliestRentalPeriod }}.</span>
      </div>
      <button mat-raised-button color="primary" [disabled]="!canBillBack" (click)="billBackPeriods()">
        <mat-spinner *ngIf="backBilling" diameter="18"></mat-spinner>
        Bill from {{ earliestRentalPeriod }}
      </button>
    </div>

    <div class="bt-alert bt-alert--warn bt-missing" *ngIf="missingByProperty.length">
      <mat-icon>warning_amber</mat-icon>
      <div class="bt-missing__body">
        <strong>Some properties are missing from existing returns.</strong>
        <ul>
          <li *ngFor="let g of missingByProperty">
            {{ g.address }}:
            <span *ngIf="g.add.length">will be added to {{ g.add.join(', ') }}</span>
            <span *ngIf="g.add.length && g.supplemental.length">; </span>
            <span *ngIf="g.supplemental.length">{{ g.supplemental.join(', ') }} already paid — a separate supplemental return will be created</span>
            <span *ngIf="g.conflict.length">{{ (g.add.length || g.supplemental.length) ? '; ' : '' }}{{ g.conflict.join(', ') }} crosses a shared-property boundary — not billed for this property; replace that return with aligned ones (contact support)</span>
          </li>
        </ul>
      </div>
      <button mat-raised-button color="primary" [disabled]="busy" (click)="attachProperties()">
        <mat-spinner *ngIf="attaching" diameter="18"></mat-spinner>
        Add to existing returns
      </button>
    </div>

    <!-- Filing schedule -->
    <section class="bt-card">
      <div class="bt-card__head">
        <mat-icon>calendar_month</mat-icon>
        <div>
          <h4>Filing schedule</h4>
          <p class="bt-sub">Frequency, period range, and due date.</p>
        </div>
      </div>
      <div class="bt-grid">
        <mat-form-field appearance="outline">
          <mat-label>Frequency</mat-label>
          <mat-select [(ngModel)]="form.frequency">
            <mat-option *ngFor="let f of frequencies" [value]="f">{{ f }}</mat-option>
          </mat-select>
          <mat-icon matSuffix class="bt-help-icon" matTooltipPosition="above"
                    matTooltip="How often returns are generated and due for this account (monthly, quarterly, annual, or seasonal).">info_outline</mat-icon>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Status</mat-label>
          <mat-select [(ngModel)]="form.status">
            <mat-option *ngFor="let s of statuses" [value]="s">{{ s }}</mat-option>
          </mat-select>
          <mat-icon matSuffix class="bt-help-icon" matTooltipPosition="above"
                    matTooltip="Billing status for this account; only Active accounts can generate new returns.">info_outline</mat-icon>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Due day of month</mat-label>
          <input matInput type="number" min="1" max="28" [(ngModel)]="form.dueDayOfMonth">
          <span matTextSuffix>day</span>
          <mat-icon matSuffix class="bt-help-icon" matTooltipPosition="above"
                    matTooltip="Day of the month each return is due.">info_outline</mat-icon>
        </mat-form-field>

        <div class="bt-field">
          <mat-form-field appearance="outline">
            <mat-label>Start period</mat-label>
            <input matInput [(ngModel)]="form.startPeriod" placeholder="YYYY-MM" required
                   (blur)="startPeriodTouched = true"
                   (ngModelChange)="startPeriodServerError = null">
            <mat-icon matSuffix class="bt-help-icon" matTooltipPosition="above"
                      matTooltip="First filing period (YYYY-MM) this billing schedule starts generating returns for.">info_outline</mat-icon>
            <mat-hint>Required · YYYY-MM</mat-hint>
          </mat-form-field>
          <div class="bt-field-error" *ngIf="startPeriodError">{{ startPeriodError }}</div>
        </div>

        <mat-form-field appearance="outline">
          <mat-label>End period</mat-label>
          <input matInput [(ngModel)]="form.endPeriod" placeholder="YYYY-MM">
          <mat-icon matSuffix class="bt-help-icon" matTooltipPosition="above"
                    matTooltip="Last filing period (YYYY-MM) to generate returns for; leave blank for an ongoing schedule.">info_outline</mat-icon>
          <mat-hint>Optional · leave blank for ongoing</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" *ngIf="isSeasonal">
          <mat-label>Active months</mat-label>
          <input matInput [(ngModel)]="form.activeMonths" placeholder="e.g. 1,2,3,12">
          <mat-icon matSuffix class="bt-help-icon" matTooltipPosition="above"
                    matTooltip="Comma-separated list of months (1-12) this seasonal account files returns for.">info_outline</mat-icon>
          <mat-hint>Comma-separated, 1–12</mat-hint>
        </mat-form-field>
      </div>
    </section>

    <!-- Allowance and penalty sit side by side, each boxed (QA 2026-09-29). -->
    <div class="bt-pair">
    <!-- Collection allowance -->
    <section class="bt-card bt-card--boxed">
      <div class="bt-card__head">
        <mat-icon>savings</mat-icon>
        <div>
          <h4>Collection allowance</h4>
          <p class="bt-sub">Discount granted for on-time online filing.</p>
        </div>
        <mat-checkbox class="bt-card__toggle" color="primary" [(ngModel)]="form.collectionAllowanceEnabled">Enabled</mat-checkbox>
      </div>
      <div class="bt-grid" [class.bt-grid--off]="!form.collectionAllowanceEnabled">
        <mat-form-field appearance="outline">
          <mat-label>Rate</mat-label>
          <input matInput type="number" step="0.001" [(ngModel)]="form.collectionAllowanceRate"
                 [disabled]="!form.collectionAllowanceEnabled">
          <mat-icon matSuffix class="bt-help-icon" matTooltipPosition="above"
                    matTooltip="Percentage of tax due credited back when the return is filed and paid on time online.">info_outline</mat-icon>
          <mat-hint>Fraction, e.g. 0.025 = 2.5%</mat-hint>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Cap</mat-label>
          <input matInput type="number" step="0.01" [(ngModel)]="form.collectionAllowanceCap"
                 [disabled]="!form.collectionAllowanceEnabled">
          <span matTextPrefix>$&nbsp;</span>
          <mat-icon matSuffix class="bt-help-icon" matTooltipPosition="above"
                    matTooltip="Maximum dollar amount the collection allowance can reduce from a single return.">info_outline</mat-icon>
        </mat-form-field>
      </div>
    </section>

    <!-- Late penalty & interest -->
    <section class="bt-card bt-card--boxed">
      <div class="bt-card__head">
        <mat-icon>gavel</mat-icon>
        <div>
          <h4>Late penalty &amp; interest</h4>
          <p class="bt-sub">Applied to overdue returns.</p>
        </div>
        <mat-checkbox class="bt-card__toggle" color="primary" [(ngModel)]="form.latePenaltyEnabled">Enabled</mat-checkbox>
      </div>
      <div class="bt-grid" [class.bt-grid--off]="!form.latePenaltyEnabled">
        <mat-form-field appearance="outline">
          <mat-label>Late rate (annual)</mat-label>
          <input matInput type="number" step="0.01" [(ngModel)]="form.latePenaltyRate"
                 [disabled]="!form.latePenaltyEnabled">
          <mat-icon matSuffix class="bt-help-icon" matTooltipPosition="above"
                    matTooltip="Annual rate. Late charges accrue (rate ÷ 12) of the tax for every month (30 days or part) past due. The minimum is owed from the first day late; Late Interest shows only what the accrual adds above the minimum.">info_outline</mat-icon>
          <mat-hint>0.10 = 10% a year</mat-hint>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Penalty minimum</mat-label>
          <input matInput type="number" step="0.01" [(ngModel)]="form.latePenaltyMin"
                 [disabled]="!form.latePenaltyEnabled">
          <span matTextPrefix>$&nbsp;</span>
          <mat-icon matSuffix class="bt-help-icon" matTooltipPosition="above"
                    matTooltip="Minimum dollar penalty charged on a late return, applied even if the calculated penalty is smaller.">info_outline</mat-icon>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Penalty maximum</mat-label>
          <input matInput type="number" step="0.001" [(ngModel)]="form.latePenaltyMaxPct"
                 [disabled]="!form.latePenaltyEnabled">
          <mat-icon matSuffix class="bt-help-icon" matTooltipPosition="above"
                    matTooltip="Maximum penalty allowed, expressed as a percentage of the tax due on the return.">info_outline</mat-icon>
          <mat-hint>Fraction of tax due · 0 = no maximum</mat-hint>
        </mat-form-field>
      </div>
      <div class="bt-toggle-row">
        <mat-checkbox color="primary" [(ngModel)]="form.lateInterestEnabled">Late interest (FL floating rate)</mat-checkbox>
        <span class="bt-sub">Statutory interest accrues on overdue balances.</span>
        <!-- QA 2026-10-04: explain the statute; click works on touch too. -->
        <mat-icon class="bt-help-icon" tabindex="0" role="button" aria-label="About late interest"
                  #interestTip="matTooltip" (click)="interestTip.toggle()" (keydown.enter)="interestTip.toggle()"
                  matTooltipPosition="above" matTooltipClass="bt-tooltip-wide"
                  matTooltip="Florida statutory interest (Fla. Stat. §213.235, applied to tourist development tax by §125.0104). The rate floats: the Department of Revenue sets it every January 1 and July 1 (prime rate + 4%, see Admin > Interest rates). It accrues daily on the unpaid tax from the day after the due date until paid, and is separate from the late penalty — the penalty is 10% of tax per 30 days late (minimum $50, maximum 50%). Leave unchecked if the county does not charge interest.">info_outline</mat-icon>
      </div>
    </section>
    </div>

    <!-- Inline feedback -->
    <div class="bt-alert bt-alert--error" *ngIf="saveError"><mat-icon>error_outline</mat-icon><span>{{ saveError }}</span></div>
    <div class="bt-alert bt-alert--ok" *ngIf="saved"><mat-icon>check_circle</mat-icon><span>Billing configuration saved.</span></div>
    <div class="bt-alert bt-alert--info" *ngIf="generateMessage"><mat-icon>info</mat-icon><span>{{ generateMessage }}</span></div>

    <!-- Action bar -->
    <footer class="bt-actions">
      <div class="bt-actions__info" *ngIf="config?.lastGeneratedPeriod">
        <mat-icon>history</mat-icon> Last generated period <strong>{{ config?.lastGeneratedPeriod }}</strong>
      </div>
      <div class="bt-actions__spacer"></div>
      <button mat-stroked-button color="primary" [disabled]="!canGenerate || busy" (click)="generateFirstPeriod()">
        <mat-spinner *ngIf="generating" diameter="18"></mat-spinner>
        <mat-icon *ngIf="!generating">playlist_add</mat-icon>
        Generate returns
      </button>
      <button mat-raised-button color="primary" [disabled]="busy" (click)="save()">
        <mat-icon>save</mat-icon> Save changes
      </button>
    </footer>
  </div>

  <app-account-credit-panel *ngIf="account?.id" [accountId]="account.id!"></app-account-credit-panel>
</div>
